import React, { PropsWithChildren } from "react"
import { Grid, Typography, TableHead, Table, TableBody, TableRow, TableCell } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import flattenChildren from "react-keyed-flatten-children"
import { InvoiceLinesType, SubscriptionInvoiceLines, TransactionInvoiceLines } from "../adminInvoicingTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { toCurrency, toDinero, toDineroOrUndefined, zeroDinero } from "../../../../currency/currencyLib"
import { calculateTotals } from "../helpers/calculateTotals"
import { useMoney } from "../../../../currency/useMoney"
import { theme } from "../../../../theme/theme"

const useStyles = makeStyles()(() => ({
    tableHead: {
        background: theme.palette.tailwind.slate[100],
        th: {
            fontWeight: "600",
        },
    },
    tableRow: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    tableCell: {
        padding: ".75rem 1.5rem",
        fontSize: "0.875rem",
    },
    infoItemRow: {
        borderBottom: 0,
        padding: 0,
    },
    totalBox: {
        display: "grid",
        gap: "0.75rem",
        minWidth: "20rem",
        marginLeft: "auto",
        padding: "0 1.5rem .75rem 0",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[500]}`,
        color: theme.palette.tailwind.slate[700],
    },
}))

type InvoiceLinesSectionTableProps = Readonly<{
    invoiceLines: (TransactionInvoiceLines | SubscriptionInvoiceLines)[]
}>

export function InvoiceLinesSectionTable({ invoiceLines }: InvoiceLinesSectionTableProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const totals = calculateTotals(invoiceLines)

    return (
        <Grid container flexDirection="column" gap={5}>
            <Table>
                <TableHead className={classes.tableHead}>
                    <InvoiceLinesItemRow>
                        <LocalizedStrict id="support-reports-invoicing-table-header-product-no">
                            <span>Product no.</span>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-table-header-name">
                            <span>Name</span>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-table-header-price">
                            <Typography variant="inherit" textAlign="right">
                                Price
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-table-header-items">
                            <Typography variant="inherit" textAlign="center">
                                Items
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-table-header-total">
                            <Typography variant="inherit" textAlign="right">
                                Total
                            </Typography>
                        </LocalizedStrict>
                    </InvoiceLinesItemRow>
                </TableHead>
                <TableBody>
                    {invoiceLines.map((el, index) => {
                        const price = toDineroOrUndefined(el.price)
                        return (
                            <InvoiceLinesItemRow key={index}>
                                <>{el.type === InvoiceLinesType.TRANSACTION ? el?.productNumber : "-"}</>
                                <>{el.name}</>
                                <Typography variant="inherit" textAlign="right">
                                    {el.type === InvoiceLinesType.TRANSACTION
                                        ? price && moneyFactory.format(price)
                                        : price && moneyFactory.formatWithUnit(price, el.unit)}
                                </Typography>
                                <Typography variant="inherit" textAlign="center">
                                    {el.item}
                                </Typography>
                                <Typography variant="inherit" textAlign="right">
                                    {moneyFactory.formatAsNumber(toDinero(el.totalAmount))}
                                </Typography>
                            </InvoiceLinesItemRow>
                        )
                    })}
                </TableBody>
            </Table>
            <Grid className={classes.totalBox}>
                <Grid container justifyContent="space-between">
                    <LocalizedStrict id="support-reports-invoicing-table-bottom-transactions">
                        <Typography variant="body2">Transactions</Typography>
                    </LocalizedStrict>
                    <Typography variant="body2">
                        {moneyFactory.formatAsNumber(
                            totals[InvoiceLinesType.TRANSACTION] ?? zeroDinero(toCurrency("DKK")),
                        )}
                    </Typography>
                </Grid>
                <Grid container justifyContent="space-between" gap={1}>
                    <LocalizedStrict id="support-reports-invoicing-table-bottom-subscriptions">
                        <Typography variant="body2">Subscriptions</Typography>
                    </LocalizedStrict>
                    <Typography variant="body2">
                        {moneyFactory.formatAsNumber(
                            totals[InvoiceLinesType.SUBSCRIPTION] ?? zeroDinero(toCurrency("DKK")),
                        )}
                    </Typography>
                </Grid>
                <Grid container justifyContent="space-between">
                    <LocalizedStrict id="support-reports-invoicing-table-bottom-total">
                        <Typography variant="body2" fontWeight={700}>
                            Total
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="body2" fontWeight={700}>
                        {moneyFactory.formatAsNumber(totals.overallTotal ?? zeroDinero(toCurrency("DKK")))}
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    )
}
type InvoiceLinesItemRowProps = Readonly<
    PropsWithChildren<{
        description?: React.ReactNode
        hide?: boolean
        hideLastRow?: boolean
    }>
>
export function InvoiceLinesItemRow({ hide = false, children }: InvoiceLinesItemRowProps) {
    if (hide) return null

    const { classes, cx } = useStyles()
    const [...rest] = flattenChildren(children)

    return (
        <TableRow className={classes.tableRow}>
            {rest.map((child, index) => (
                <TableCell align="left" key={index} className={cx(classes.infoItemRow, classes.tableCell)}>
                    {child}
                </TableCell>
            ))}
        </TableRow>
    )
}
